import { useMemo, useState } from 'react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SelectField,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import { Search } from 'lucide-react';

const PAGE_SIZE = 50;
const MEMBER_ROLES = ['owner', 'admin', 'manager', 'member', 'viewer'] as const;

interface AddGroupMemberDialogProps {
  groupId: string;
  memberUserIds: Set<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddGroupMemberDialog({
  groupId,
  memberUserIds,
  open,
  onOpenChange,
}: AddGroupMemberDialogProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<string>('member');
  const list = hooks.useAdminUsers({ q, page, pageSize: PAGE_SIZE });
  const add = hooks.useAddGroupMember();

  const candidates = useMemo(() => {
    const rows = list.data?.data ?? [];
    return rows.filter((u) => !memberUserIds.has(u.id));
  }, [list.data?.data, memberUserIds]);

  const userOptions = candidates.map((u) => ({
    value: u.id,
    label: [u.name, u.surname].filter(Boolean).join(' ') || u.email || u.phone,
    description: u.email || u.phone || undefined,
  }));

  const roleOptions = MEMBER_ROLES.map((r) => ({
    value: r,
    label: t(`group.role.${r}`),
  }));

  const submit = async () => {
    if (!userId) {
      toast.error(t('group.member.selectRequired'));
      return;
    }
    try {
      await add.mutateAsync({ groupId, userId, role });
      toast.success(t('group.member.added'));
      setUserId(undefined);
      setRole('member');
      setQ('');
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('group.member.addTitle')}</DialogTitle>
          <DialogDescription>{t('group.member.addDescription')}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
          <Input
            className="pl-9"
            placeholder={t('actions.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <SelectField
          value={userId}
          onValueChange={setUserId}
          options={userOptions}
          placeholder={t('group.member.selectPlaceholder')}
          disabled={list.isLoading || userOptions.length === 0}
        />
        <div className="space-y-1.5">
          <Label>{t('group.role.label')}</Label>
          <SelectField value={role} onValueChange={setRole} options={roleOptions} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={add.isPending}>
            {t('group.actions.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
